import { CDMO_STATUS, type CDMOFacility } from '../../constants';
import type { CDMOApplication } from '../supabase';
import type { CdmoImportProfile, CdmoImportReview } from './validateRow';

export interface CdmoImportContext {
  /** Name of the uploaded file, recorded in the profile's admin notes. */
  fileName: string;
  /** Local date of the import, YYYY-MM-DD. */
  importedOn: string;
  adminEmail: string;
}

export const NOT_VERIFIED_AT_IMPORT_NOTES = {
  willingness_feasibility_studies: 'Willing to do feasibility studies: not verified at import',
  startup_friendly: 'Startup friendly: not verified at import',
} as const;

/** Today's date in the admin's time zone, YYYY-MM-DD. */
export function localIsoDate(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The profile's `admin_notes`: parent company, the CSV's own notes, which yes/no answers were not
 * verified, and where the profile came from — each part separated by a blank line, empty parts
 * skipped.
 */
export function buildImportAdminNotes(
  profile: CdmoImportProfile,
  review: CdmoImportReview,
  context: CdmoImportContext
): string {
  const parts = [
    profile.parent_company ? `Parent company: ${profile.parent_company}` : '',
    profile.admin_notes,
    profile.willingness_feasibility_studies === null ? NOT_VERIFIED_AT_IMPORT_NOTES.willingness_feasibility_studies : '',
    profile.startup_friendly === null ? NOT_VERIFIED_AT_IMPORT_NOTES.startup_friendly : '',
    `Imported from ${context.fileName} on ${context.importedOn} by ${context.adminEmail} — ` +
      `research date ${review.research_date}, collected via ${review.collected_via}. ` +
      `Review summary: ${review.review_summary}`,
  ];
  return parts.filter(Boolean).join('\n\n');
}

/**
 * The profile form stores a blank contact field as NULL (its submission object is untyped), while
 * `CDMOApplication` types these fields as strings. Mirror the form rather than writing ''.
 */
const blankAsNull = (value: string) => (value || null) as unknown as string;

/**
 * Builds the `CDMOApplication` for one validated row: an unclaimed, admin-created profile with no
 * owner, exactly as the admin "Add New" form creates one. `dbService.createCDMOApplication` then
 * adds the claim tokens and writes it through `buildCdmoApplicationRow`.
 */
export function mapToApplication(
  profile: CdmoImportProfile,
  review: CdmoImportReview,
  context: CdmoImportContext,
  newFacilityId: () => string = () => crypto.randomUUID()
): CDMOApplication {
  const facilities: CDMOFacility[] = profile.facilities.map((facility) => ({
    id: newFacilityId(),
    region: facility.region,
    location: facility.location,
    capabilities: facility.capabilities,
    equipment: facility.equipment,
  }));

  return {
    // No owner, no uploaded file and no free-text facility details: buildCdmoApplicationRow writes
    // each of these undefined values as NULL.
    user_id: undefined,
    file_name: undefined,
    facility_locations_details: undefined,
    status: CDMO_STATUS.UNCLAIMED,
    is_admin_created: true,
    locked_fields: [],

    company_name: profile.company_name,
    website: profile.website,
    cdmo_business_profile: profile.cdmo_business_profile ?? undefined,
    contact_name: blankAsNull(profile.contact_name),
    // `email` is the owner's account email, filled in when the profile is claimed; until then it is
    // empty and `primary_email` is who the claim invitation goes to — how the form stores every
    // unclaimed profile, and a rule of its edit validation (the two must differ).
    email: blankAsNull(''),
    primary_email: profile.email || undefined,
    phone: blankAsNull(profile.phone),
    secondary_emails: profile.secondary_emails,
    // Joined the way the form joins them, so the unique constraint on fei_number compares like with like.
    fei_number: profile.fei_numbers.join(',') || undefined,
    description: profile.description || undefined,
    primary_capabilities: profile.primary_capabilities,
    detailed_capabilities: profile.detailed_capabilities,
    typical_batch_sizes: profile.typical_batch_sizes,
    budget_ranges: profile.budget_ranges,
    target_client_types: profile.target_client_types,
    engagement_scales: profile.engagement_scales,
    preferred_project_types: profile.preferred_project_types,
    gmp_certifications: profile.gmp_certifications,
    operating_regions: profile.operating_regions,
    facilities,
    speed_to_start: profile.speed_to_start ?? undefined,
    willingness_feasibility_studies: profile.willingness_feasibility_studies ?? false,
    startup_friendly: profile.startup_friendly ?? false,
    admin_notes: buildImportAdminNotes(profile, review, context),
  };
}
